/**
 * @file TilesElementPanel.tsx
 * @description Панель «Плитки-ссылки» (phase19.16 Р2; uiux-design-r2 §4):
 * до 12 плиток (перетаскиваются) — фото, заголовок, строка текста, текст
 * кнопки, куда ведёт; в ряд 1–8, вид [Текст поверх фото][Фото
 * сверху], на телефоне [Стопкой][Лентой][По две в ряд].
 *
 * SB24, дополнение 2026-10-11: у «Текста поверх фото» — тон текста (светлый
 * с затемнением / тёмный без затемнения), у любого вида — высота плитки
 * (SB120 этап 3а: пропорцией 0,5–2, `TileAspectField`).
 *
 * SB120 этап 3б (uiux-sb120-stage3 §3): в ряд 1–8 числом с подписью «на
 * планшете N» (`GridCountField`), затемнение 0–80 % у «Текста поверх фото»
 * (`TileShadeField`, ↺ по набору), на телефоне схемами (`PhoneGridField`).
 */

import {
  SECTION_TEXT_LIMITS,
  SITE_LIMITS,
  TILE_GRID_COLS,
  TILE_TONES,
  defaultElementBody,
  TILE_TONE_LABEL,
  TILE_TONE_OPTIONS,
  type SectionElementOf,
} from "@chaiberi/shared";

import { Label } from "../../../ui/label";
import { Segmented } from "../../../site-view/Segmented";
import { MediaIdField } from "../fields/MediaIdField";
import { SectionTargetField } from "../fields/SectionTargetField";
import { SectionTextField } from "../fields/SectionTextField";
import { SortableList } from "../fields/SortableList";
import { useSectionForm } from "../section-form-context";
import { GridCountField } from "./GridCountField";
import { PhoneGridField } from "./PhoneGridField";
import { TileAspectField } from "./TileAspectField";
import { TileShadeField } from "./TileShadeField";
import type { ElementPanelProps } from "./types";

type Tile = SectionElementOf<"tiles">["items"][number];
const T = SECTION_TEXT_LIMITS;

const TONES = TILE_TONES.map((value) => ({ value, label: TILE_TONE_OPTIONS[value] }));

const DEFAULT_COLS = defaultElementBody("tiles").cols;
export const TILE_TONE_HINT = "Тёмный — для светлых картинок без затемнения";

export function TilesElementPanel({ element, onChange, path }: ElementPanelProps<"tiles">) {
  const { hasIssuesUnder } = useSectionForm();
  const set = (patch: Partial<typeof element>) => onChange({ ...element, ...patch });
  return (
    <>
      <SortableList<Tile>
        label="Плитки"
        items={element.items}
        max={SITE_LIMITS.tiles}
        addLabel="Плитка"
        makeItem={() => ({ mediaId: null, title: "Название", target: null })}
        onChange={(items) => set({ items })}
        invalidAt={(i) => hasIssuesUnder(`${path}.items.${i}`)}
        summary={(tile, i) => tile.title || `Плитка ${i + 1}`}
        renderItem={(tile, update, i) => (
          <>
            <MediaIdField label="Фото" value={tile.mediaId} onChange={(mediaId) => update({ ...tile, mediaId })} />
            <SectionTextField label="Заголовок" path={`${path}.items.${i}.title`} value={tile.title} max={T.tileTitle} onChange={(title) => update({ ...tile, title })} />
            <SectionTextField label="Текст (до 2 строк)" path={`${path}.items.${i}.text`} value={tile.text} max={T.tileText} onChange={(text) => update({ ...tile, text: text || undefined })} />
            <SectionTextField label="Текст кнопки" path={`${path}.items.${i}.buttonLabel`} value={tile.buttonLabel} max={T.label} hint="Пусто — вся плитка ссылка." onChange={(b) => update({ ...tile, buttonLabel: b || undefined })} />
            <SectionTargetField optional value={tile.target} onChange={(target) => update({ ...tile, target })} />
          </>
        )}
      />
      <GridCountField kind="tiles" field="cols" values={TILE_GRID_COLS} value={element.cols} fallback={DEFAULT_COLS} path={path} onChange={(cols) => set({ cols: cols ?? DEFAULT_COLS })} />
      <Label>Вид</Label>
      <Segmented
        aria-label="Вид плиток"
        options={[
          { value: "overlay" as const, label: "Текст поверх фото" },
          { value: "below" as const, label: "Фото сверху" },
        ]}
        value={element.look}
        onChange={(look) => set({ look })}
      />
      {element.look === "overlay" ? (
        <>
          <Label>{TILE_TONE_LABEL}</Label>
          <Segmented aria-label={TILE_TONE_LABEL} options={TONES} value={element.tone} onChange={(tone) => set({ tone })} />
          <p className="type-caption text-on-surface-variant">{TILE_TONE_HINT}</p>
          <TileShadeField
            tone={element.tone}
            value={element.shade}
            path={path}
            onChange={(shade) => {
              const next = { ...element };
              if (shade === undefined) delete next.shade;
              else next.shade = shade;
              onChange(next);
            }}
          />
        </>
      ) : null}
      <TileAspectField value={element.aspect} path={path} onChange={(aspect) => set({ aspect })} />
      <PhoneGridField ariaLabel="Плитки на телефоне" value={element.phoneLayout} onChange={(phoneLayout) => set({ phoneLayout })} />
    </>
  );
}
